import { illustrationFor } from "@shared/brand.types";
import { ECOLE_LIMITES, type EcoleConfig, type EcoleSchool } from "@shared/ecole.types";
import { ExternalLink, ListChecks, Lock, Palette, Type, UserRound } from "lucide-react";
import { buttonClasses, buttonLabelClasses } from "@/components/ds/Button";
import { Card, CardHeader, ReadOnlyBlock } from "@/components/ds/Card";
import { TextAreaField, TextField } from "@/components/ds/Fields";
import { FormFieldsEditor } from "./FormFieldsEditor";
import { NavColorField } from "./NavColorField";

/**
 * Ce qu'une école règle elle-même, et ce qui ne se règle pas ici.
 *
 * L'écran est ordonné par ce que l'entreprise VOIT, du plus visible au moins :
 * la couleur, puis les textes de la page de dépôt, puis l'interlocuteur, puis les
 * champs du formulaire. Il finit par ce qui vient de main-app et reste en lecture
 * seule — le dire est plus utile que de laisser chercher.
 *
 * Trois gestes de composition repris des écrans de réglages du proto : une
 * PASTILLE d'icône par carte, une grille à DEUX colonnes pour les champs courts,
 * et un bloc ENCASTRÉ (le beige de page dans la carte blanche) pour tout ce qui se
 * lit sans se modifier. Sans eux, cinq cartes empilées se lisent comme un
 * document, pas comme un écran.
 */
export function ConfigPage({
	school,
	onSave,
}: {
	school: EcoleSchool;
	onSave: (patch: EcoleConfig) => Promise<boolean>;
}) {
	const lienPublic = `/e/${school.slug}`;

	return (
		<div className="flex flex-col gap-6">
			<header className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="t-h1 text-mg-heading">Ma page de dépôt</h1>
					<p className="t-body-lg mt-1 text-mg-secondary">
						C'est la page que vos entreprises ouvrent pour vous envoyer une offre.
					</p>
				</div>
				<a
					href={lienPublic}
					target="_blank"
					rel="noreferrer"
					className={buttonClasses({ tone: "secondary", size: "sm" })}
				>
					<span className={buttonLabelClasses("sm")}>Voir ma page</span>
					<ExternalLink className="size-4" aria-hidden="true" />
				</a>
			</header>

			<Card padded={false}>
				<CardHeader
					icon={<Palette aria-hidden="true" />}
					title="Vos couleurs"
					subtitle="Chaque réglage s'enregistre aussitôt."
				/>
				<div className="p-6">
					<NavColorField school={school} onSave={(navBase) => onSave({ navBase })} />
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader
					icon={<Type aria-hidden="true" />}
					title="Vos textes"
					subtitle="Laissez vide pour garder les textes Mentor Goal."
				/>
				{/* Les champs courts se rangent à deux ; les zones de texte longues gardent
				    la pleine largeur, sinon on lit sur deux colonnes étroites. */}
				<div className="grid gap-4 p-6 md:grid-cols-2">
					<TextField
						label="Titre de la page"
						hint="Par exemple : Recrutez un alternant avec nous."
						max={ECOLE_LIMITES.pageTitle}
						value={school.pageTitle ?? ""}
						onSave={(pageTitle) => onSave({ pageTitle })}
					/>
					<TextField
						label="Baseline"
						hint="Une ligne sous le nom de votre école."
						max={ECOLE_LIMITES.baseline}
						value={school.baseline ?? ""}
						onSave={(baseline) => onSave({ baseline })}
					/>
					<div className="md:col-span-2">
						<TextAreaField
							label="Phrase d'accueil"
							hint="Ce que l'entreprise lit sous le titre, avant de remplir."
							max={ECOLE_LIMITES.pageIntro}
							rows={4}
							value={school.pageIntro ?? ""}
							onSave={(pageIntro) => onSave({ pageIntro })}
						/>
					</div>
					<div className="md:col-span-2">
						<TextAreaField
							label="Message après l'envoi"
							hint="Dites ce qui se passe ensuite, et sous quel délai."
							max={ECOLE_LIMITES.confirmationMessage}
							rows={3}
							value={school.confirmationMessage ?? ""}
							onSave={(confirmationMessage) => onSave({ confirmationMessage })}
						/>
					</div>
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader
					icon={<UserRound aria-hidden="true" />}
					title="Votre interlocuteur"
					subtitle="La personne que l'entreprise contacte pour cette page."
				/>
				<div className="grid gap-4 p-6 md:grid-cols-2">
					<TextField
						label="Nom"
						max={ECOLE_LIMITES.contactName}
						value={school.contactName ?? ""}
						onSave={(contactName) => onSave({ contactName })}
					/>
					<TextField
						label="Email"
						value={school.contactEmail ?? ""}
						onSave={(contactEmail) => onSave({ contactEmail })}
					/>
					<TextField
						label="Téléphone"
						max={ECOLE_LIMITES.contactPhone}
						value={school.contactPhone ?? ""}
						onSave={(contactPhone) => onSave({ contactPhone })}
					/>
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader
					icon={<ListChecks aria-hidden="true" />}
					title="Votre formulaire"
					subtitle="Ce que vous demandez à l'entreprise au moment du dépôt."
				/>
				<FormFieldsEditor
					value={school.formFields}
					onSave={(formFields) => onSave({ formFields })}
				/>
			</Card>

			<Card padded={false}>
				<CardHeader
					icon={<Lock aria-hidden="true" />}
					title="Ce qui vient de Mentor Goal"
					subtitle="Votre logo et votre image viennent de votre espace école."
					action={
						<span className="t-caption flex items-center gap-1.5 text-mg-secondary">
							{/* Le seul cadenas de l'écran qui n'était pas masqué. Sans nom
							    accessible, il n'était de toute façon pas annoncé : `aria-hidden`
							    aligne l'écriture sur les autres, il ne retire pas une annonce. */}
							<Lock className="size-3.5" aria-hidden="true" />
							Lecture seule
						</span>
					}
				/>
				<div className="grid gap-4 p-6 md:grid-cols-2">
					<ReadOnlyBlock label="Adresse de votre page">
						<span className="break-all">{lienPublic}</span>
					</ReadOnlyBlock>
					<ReadOnlyBlock label="Nom affiché">{school.schoolName}</ReadOnlyBlock>
					<div className="flex flex-wrap items-center gap-4 md:col-span-2">
						{school.logoUrl ? (
							<figure>
								<img
									src={school.logoUrl}
									alt=""
									className="h-14 w-14 rounded-md border border-mg-border bg-mg-surface object-contain p-1"
								/>
								<figcaption className="t-caption mt-1 text-mg-secondary">Logo</figcaption>
							</figure>
						) : null}
						<figure>
							<img
								src={illustrationFor(school)}
								alt=""
								className="h-14 w-24 rounded-md border border-mg-border object-cover"
							/>
							<figcaption className="t-caption mt-1 text-mg-secondary">Image de la page</figcaption>
						</figure>
					</div>
				</div>
			</Card>
		</div>
	);
}
